import { createColumnHelper } from '@tanstack/react-table';
import { Button, RadioButton } from '@carbon/react';
import {
  ChevronDown,
  ChevronRight,
  DataShare,
  Edit,
  Renew,
  TrashCan,
} from '@carbon/react/icons';
import type { Wellbore } from 'types/api-responses';
import styles from './wellbores-table.module.scss';

// Story 7's two additions to the Wellbores table, both opt-in per host: the chevron
// that opens a row into its points, and the pencil that opens the geometry tearsheet.
const columnHelper = createColumnHelper<Wellbore>();

/**
 * What the expand state is keyed on. A UWI14 can hold more than one row — the filed
 * bore and a pre-drill stand-in — so the UWI14 alone would open both at once.
 * @param {Wellbore} row The row.
 * @return {string} The row's key.
 */
export const expandKey = (row: Wellbore): string =>
  `${row.uwi14}-${row.id}-${row.predrillType ?? 'bore'}`;

/**
 * The leading chevron that opens a row into its points (Story 7).
 * @param {Object} expanded Which rows are open, keyed by `expandKey`.
 * @param {Function} onToggle Opens or closes one row, given its key.
 * @return {Object} The column definition.
 */
export const buildExpandColumn = (
  expanded: Record<string, boolean>,
  onToggle: (key: string) => void
) =>
  columnHelper.display({
    id: 'expand',
    header: '',
    size: 44,
    enableSorting: false,
    enableHiding: false,
    cell: (info) => {
      const key = expandKey(info.row.original);
      const open = !!expanded[key];
      return (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          className={styles.expandButton}
          renderIcon={open ? ChevronDown : ChevronRight}
          iconDescription={
            open ? 'Collapse wellbore points' : 'Expand wellbore points'
          }
          tooltipPosition="right"
          aria-expanded={open}
          onClick={() => onToggle(key)}
        />
      );
    },
  });

/**
 * The geometry source a bore the LTD picker built carries — `LTDCompositeBuilder`'s
 * `GEOMETRY_SOURCE`, served verbatim on the row. Only such a bore can be REBUILT from
 * LTDs: running the picker over a filed survey or a pre-drill stand-in would replace a
 * real trajectory with a composite, and the backend does not refuse it (its own guard
 * asks only that the bore be drawn), so the gate has to be here.
 */
const LTD_COMPOSITE = 'ltd_composite';

/** What `buildPrimaryColumn` needs to render the Primary radio column. */
export interface PrimaryColumnInput {
  /** The bore the well currently derives from. */
  primaryUwi14: string | null;
  /** The bores the well has drawn. */
  drawn: ReadonlySet<string>;
  /** Switches the primary to a row's bore. */
  onSetPrimary: (row: Wellbore) => void;
  /** The bore mid-switch, if any. */
  pendingUwi14: string | null;
  /** Whether the control is held (a switch is in flight). */
  disabled: boolean;
}

/**
 * The leading Primary column: which bore the well derives from, and the control that
 * moves it.
 *
 * ONE control for both jobs, deliberately. A marker plus a separate "make primary" action
 * would be two things to keep in step, and a radio group already means exactly this —
 * one of these, and picking another moves it. It is the same reason the column has no
 * header icon or legend: a radio column reads as a single choice without being told.
 *
 * Rendered only where a host supplies `onSetPrimary` AND the primary is actually known —
 * the backend serves the pointer for an authored well only. A radio group with nothing
 * selected would claim the well has no primary, which is a different and usually false
 * statement.
 *
 * A row the well has not actually DRAWN cannot be primary, so it gets a blank cell rather
 * than a disabled radio: there is no sense in which it is a candidate. Membership is the
 * backend's own `boreCompletionUwi14s`, never a guess from the row — an authored bore is
 * routinely BOTH a drawn bore and a permit-stage row (`predrill_type: 'permit'` alongside
 * `geometry_source: 'ltd_composite'`), so inferring it from `predrillType` blanks the
 * control on exactly the rows it exists for.
 * @param {PrimaryColumnInput} input The current primary, the drawn set, the handler, the
 *   row a change is in flight for, and whether the control is disabled.
 * @return {Object} The column definition.
 */
export const buildPrimaryColumn = ({
  primaryUwi14,
  drawn,
  onSetPrimary,
  pendingUwi14,
  disabled,
}: PrimaryColumnInput) =>
  columnHelper.display({
    id: 'primary',
    header: 'Primary',
    size: 76,
    enableSorting: false,
    enableHiding: false,
    cell: (info) => {
      const row = info.row.original;
      if (!drawn.has(row.uwi14)) return null;
      const isPrimary = row.uwi14 === primaryUwi14;

      return (
        <RadioButton
          name="primary-wellbore"
          value={row.uwi14}
          checked={isPrimary}
          // Already the primary: re-picking it is a no-op the backend would still
          // process, clearing the unit pick for nothing.
          disabled={disabled || isPrimary}
          labelText={
            pendingUwi14 === row.uwi14
              ? `Making ${row.uwi14} the primary wellbore…`
              : `Make ${row.uwi14} the primary wellbore`
          }
          hideLabel
          onChange={() => onSetPrimary(row)}
        />
      );
    },
  });

/** What `buildActionsColumn` needs to render the row actions. */
export interface ActionsColumnInput {
  /** Opens the geometry workspace for a row. */
  onEditGeometry: (row: Wellbore) => void;
  /** Opens the geometry-source picker for a row. */
  onChangeGeometrySource?: (row: Wellbore) => void;
  /** Reopens the LTD picker over an existing bore. */
  onRebuildWellbore?: (row: Wellbore) => void;
  /** Asks to remove the row's bore from the well. */
  onRemoveWellbore?: (row: Wellbore) => void;
  /** The bores the well has drawn, for the remove gate. */
  drawn?: ReadonlySet<string>;
}

/**
 * The trailing Actions column: the pencil that opens the geometry tearsheet and, when a
 * Well Workspace offers them, the controls that change the row's geometry source and
 * rebuild it from LTD sticks.
 * @param {ActionsColumnInput} input The row handlers and the drawn set.
 * @return {Object} The column definition.
 */
export const buildActionsColumn = ({
  onEditGeometry,
  onChangeGeometrySource,
  onRebuildWellbore,
  onRemoveWellbore,
  drawn = new Set(),
}: ActionsColumnInput) =>
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    // Widened per offered action rather than by a pair of hardcoded numbers, so a fourth
    // does not silently crowd the cell.
    size:
      90 +
      (onChangeGeometrySource ? 30 : 0) +
      (onRebuildWellbore ? 30 : 0) +
      (onRemoveWellbore ? 30 : 0),
    enableSorting: false,
    enableHiding: false,
    cell: (info) => (
      <span className={styles.actions}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Edit}
          iconDescription="Edit wellbore geometry"
          tooltipPosition="left"
          onClick={() => onEditGeometry(info.row.original)}
        />
        {onChangeGeometrySource && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={DataShare}
            iconDescription="Change wellbore geometry source"
            tooltipPosition="left"
            onClick={() => onChangeGeometrySource(info.row.original)}
          />
        )}
        {onRebuildWellbore &&
          info.row.original.geometrySource === LTD_COMPOSITE && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Renew}
              // "Rebuild", never "Edit": the picker opens empty and REPLACES the bore's
              // geometry. The pencil beside it is the one that edits what is there.
              iconDescription="Rebuild wellbore from LTDs"
              tooltipPosition="left"
              onClick={() => onRebuildWellbore(info.row.original)}
            />
          )}
        {onRemoveWellbore && drawn.has(info.row.original.uwi14) && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={TrashCan}
            // Ghost like its three siblings, not `danger--ghost`: the weight belongs on
            // the confirm, which is where the consequences are spelled out.
            //
            // Offered on ANY drawn bore, unlike Rebuild — the backend asks only that the
            // bore be drawn, and a reader must be able to undo one whatever built it.
            // "Drawn" is the backend's own list, not a guess: a row can carry a
            // predrill_type AND be a real staged bore at the same time, so reading it off
            // the row would withhold this from the bores it is meant for.
            iconDescription="Remove wellbore"
            tooltipPosition="left"
            onClick={() => onRemoveWellbore(info.row.original)}
          />
        )}
      </span>
    ),
  });
